// Add a line to a draft invoice, or change one.
//
// The form's one real idea is that THE AMOUNT MAY BE BLANK, and that leaving it
// blank is a statement rather than an omission. "Kingsport July expenses,
// waiting on Hansen" is a line you write down in August and price in September;
// until then the document carries it, no total counts it, and the invoice
// refuses to be issued. That is the whole reason this screen exists rather than
// the row being assembled at the moment of invoicing.
//
// WHAT IT DOES NOT OFFER is the two source links. A line's identity — what it
// points at, or that it points at nothing — is decided when it is added, from
// the billing screen or from a bill run, because re-pointing an existing line
// would leave its amounts describing something else. Editing reaches the words,
// the figure and the position; changing what a line IS means removing it and
// adding the right one, which on a draft costs nothing.

import { useMemo, type ReactNode } from "react";
import {
  InvoiceLineCategory,
  InvoiceLineCreate,
  InvoiceLineUpdate,
  type InvoiceLineListItem,
  type InvoiceListItem,
} from "@crm/shared";
import { useInvoiceLineMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { NumberField, SelectField, TextField } from "../../forms/fields.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { formatMoney, minorToInput, parseMoneyToMinor } from "../../format/money.js";
import { Notice } from "../../design/Feedback.js";

interface LineDraft {
  description: string;
  category: string;
  /** A decimal string, or blank — which is how "not priced yet" is entered. */
  amount: string;
  our_share: string;
  /** The base, on a linked line. Blank means the source has no figure yet. */
  gross: string;
  /** The rate, as a plain 0-100 decimal string. Typing here IS the override. */
  split_pct: string;
  notes: string;
}

const LABELS = {
  description: "Description",
  category: "Kind",
  charged_minor: "Amount",
  our_share_minor: "Our share",
  gross_minor: "Customer billed",
  applied_split_pct: "Rev share",
  notes: "Note",
} as const;

const CATEGORY_OPTIONS = [
  { value: "", label: "— none —" },
  ...InvoiceLineCategory.options.map((category) => ({
    value: category,
    label: category.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
  })),
];

export interface InvoiceLineDialogProps {
  invoice: InvoiceListItem;
  /** Absent when adding. */
  line?: InvoiceLineListItem;
  onClose: () => void;
}

export function InvoiceLineDialog({ invoice, line, onClose }: InvoiceLineDialogProps): ReactNode {
  const mutations = useInvoiceLineMutations();
  const linked = line !== undefined && line.source_kind !== "standalone";

  const toWire = useMemo(
    () =>
      (draft: LineDraft): Wire => ({
        invoice_id: invoice.id,
        // A linked line keeps its source's label unless somebody has typed one,
        // so a blank description here means "use whatever it points at" rather
        // than "call this line nothing".
        description: draft.description.trim() === "" ? null : draft.description.trim(),
        category: draft.category === "" ? null : draft.category,
        // BLANK IS NULL, and null is the point: it says the line is real and its
        // figure is not. `parseMoneyToMinor` returns NaN on anything that is not
        // a number, which the schema then rejects by name.
        charged_minor:
          draft.amount.trim() === "" ? null : parseMoneyToMinor(draft.amount, invoice.currency),
        our_share_minor:
          draft.our_share.trim() === ""
            ? null
            : parseMoneyToMinor(draft.our_share, invoice.currency),
        gross_minor:
          draft.gross.trim() === "" ? null : parseMoneyToMinor(draft.gross, invoice.currency),
        // A rate typed here is frozen onto this line and nothing re-derives it —
        // which is what makes this the override. `Number("")` is 0 and 0 is a
        // legal rate, so blank has to be caught before the coercion rather than
        // after it.
        applied_split_pct: draft.split_pct.trim() === "" ? null : Number(draft.split_pct),
        notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
      }),
    [invoice.id, invoice.currency],
  );

  const editor = useEditor<LineDraft, InvoiceLineCreate, InvoiceLineUpdate, InvoiceLineListItem>({
    mode: line === undefined ? { kind: "create" } : { kind: "update", record: line },
    blankDraft: {
      description: "",
      category: "",
      amount: "",
      our_share: "",
      gross: "",
      split_pct: "",
      notes: "",
    },
    draftFromRecord: (record) => ({
      description: record.description ?? "",
      category: record.category ?? "",
      amount:
        record.charged_minor === null ? "" : minorToInput(record.charged_minor, invoice.currency),
      our_share:
        record.our_share_minor === null
          ? ""
          : minorToInput(record.our_share_minor, invoice.currency),
      gross: record.gross_minor === null ? "" : minorToInput(record.gross_minor, invoice.currency),
      split_pct: record.applied_split_pct === null ? "" : String(record.applied_split_pct),
      notes: record.notes ?? "",
    }),
    toWire,
    createSchema: InvoiceLineCreate,
    updateSchema: InvoiceLineUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  const mismatch = shareMismatch(editor.draft, invoice.currency);

  return (
    <FormDialog
      open
      onClose={onClose}
      title={line === undefined ? "Add a line" : "Edit this line"}
      description={
        linked
          ? "This line bills something already on the schedule. Leaving the description blank uses that row's own label."
          : "Leave the amount blank if the figure is not known yet — the line stays on the document and the invoice will not go out until it has one."
      }
      submitLabel={line === undefined ? "Add line" : "Save"}
      editor={editor}
      labels={LABELS}
    >
      <TextField
        label={LABELS.description}
        required={!linked}
        hint={
          linked
            ? "Optional. Blank uses the label of whatever this line bills."
            : "What this says on the invoice."
        }
        value={editor.draft.description}
        error={editor.errors["description"]}
        onChange={(value) => editor.setField("description", value)}
      />

      {/* Only on a standalone line: a linked one's kind is whatever it points
          at, and the database refuses a category on it by name. */}
      {!linked && (
        <SelectField
          label={LABELS.category}
          hint="What kind of charge this is, so 'what did we give away this quarter' is a query."
          value={editor.draft.category}
          options={CATEGORY_OPTIONS}
          error={editor.errors["category"]}
          onChange={(value) => editor.setField("category", value)}
        />
      )}

      {/* THE BASE AND THE RATE, above the amount they produce, because that is
          the order the arithmetic runs and the order somebody checking the line
          reads it in.

          LINKED LINES ONLY. A standalone credit or late fee was not divided out
          of anything, so a base on it would be a claim about a customer the line
          does not have — the shared schema refuses it by name, and offering a
          field the server will reject is how a form teaches the wrong model. */}
      {linked && (
        <>
          <NumberField
            label={LABELS.gross_minor}
            suffix={invoice.currency}
            hint="What the end customer was charged — the figure this line's rev share is a percentage OF. It is not what this invoice asks for."
            value={editor.draft.gross}
            error={editor.errors["gross_minor"]}
            onChange={(value) => editor.setField("gross", value)}
          />

          <NumberField
            label={LABELS.applied_split_pct}
            suffix="%"
            hint="The rate on this line. Pre-filled from the agreement in force when the bill was raised — type over it when this one bill was agreed differently, and nothing will re-derive it."
            value={editor.draft.split_pct}
            error={editor.errors["applied_split_pct"]}
            onChange={(value) => editor.setField("split_pct", value)}
          />
        </>
      )}

      <NumberField
        label={LABELS.charged_minor}
        suffix={invoice.currency}
        hint="What the payer is charged. Negative for a credit. Leave blank if you do not know yet."
        value={editor.draft.amount}
        error={editor.errors["charged_minor"]}
        onChange={(value) => editor.setField("amount", value)}
      />

      <NumberField
        label={LABELS.our_share_minor}
        suffix={invoice.currency}
        hint="What reaches us out of that. Leave blank to say nothing about it."
        value={editor.draft.our_share}
        error={editor.errors["our_share_minor"]}
        onChange={(value) => editor.setField("our_share", value)}
      />

      {/* The arithmetic, checked out loud.
          NOT an auto-fill and not a blocking error: the three figures are all
          settable on purpose — a bill agreed at a flat sum against a percentage
          line is a real thing, and 0013 spent a migration establishing that a
          bill may state a figure nobody can re-derive. So this SAYS when they
          disagree and leaves the decision where it belongs. Rounding is the
          reason for the one-cent tolerance: the database rounds half-up in
          `crm_our_share_minor`, and re-deriving it here would disagree with it
          on exactly the amounts that end in a half cent. */}
      {mismatch !== null && (
        <Notice tone="warn" title="These three numbers do not agree.">
          {mismatch}
        </Notice>
      )}

      <TextField
        label={LABELS.notes}
        hint="Anything a person needs to know about this line before the invoice goes out."
        value={editor.draft.notes}
        error={editor.errors["notes"]}
        onChange={(value) => editor.setField("notes", value)}
      />
    </FormDialog>
  );
}

/**
 * Whether the base, the rate and our share tell the same story — as a sentence
 * to show, or null when they do.
 *
 * Says nothing unless all three are present: a line still being typed is not
 * wrong, it is unfinished, and a form that starts complaining at the first
 * keystroke gets ignored by the third.
 *
 * ONE CENT of tolerance, because `crm_our_share_minor` rounds half-up in
 * Postgres and this re-derivation would otherwise disagree with the database on
 * every amount that lands on a half cent — reporting a mismatch that only ever
 * existed in the check itself.
 */
function shareMismatch(draft: LineDraft, currency: InvoiceListItem["currency"]): string | null {
  if (draft.gross.trim() === "" || draft.split_pct.trim() === "" || draft.our_share.trim() === "") {
    return null;
  }
  const gross = parseMoneyToMinor(draft.gross, currency);
  const share = parseMoneyToMinor(draft.our_share, currency);
  const pct = Number(draft.split_pct);
  if (!Number.isFinite(gross) || !Number.isFinite(share) || !Number.isFinite(pct)) return null;

  const expected = Math.round((gross * pct) / 100);
  if (Math.abs(expected - share) <= 1) return null;
  return `${formatMoney(gross, currency, { omitCurrencyCode: true })} at ${String(pct)}% is ${formatMoney(expected, currency, { omitCurrencyCode: true })}, and this line says our share is ${formatMoney(share, currency, { omitCurrencyCode: true })}. That is legitimate when this bill was agreed at a flat sum — otherwise one of the three is wrong.`;
}
